import { metricKeys } from '@pulse/metrics';

// preview images
import PreviewNetworkPercentile from '@images/widgets/eco-nw-percentile.png';
import PreviewNetworkDuration from '@images/widgets/eco-nw-duration.png';
import PreviewNetworkResponse from '@images/widgets/eco-nw-response.png';
import PreviewANR from '@images/widgets/dpi-anr.png';
import PreviewCrashes from '@images/widgets/dpi-crashes.png';
import PreviewOpp from '@images/widgets/video-opp.png';
import PreviewMetricGroup from '@images/widgets/metric-group.png';
import PreviewRCA from '@images/widgets/eco-auto-rca.png';
import PreviewFlow from '@images/widgets/eco-flow.png';
import PreviewQoE from '@images/widgets/eco-qoe.png';
import PreviewImpactedTime from '@images/widgets/eco-impacted-time.png';
import PreviewEvent from '@images/widgets/eco-event.png';
import { buildWidgetConfig } from '../state/utils';
import { AVAILABILITY, GLOBAL_MAX_WIDGETS_ALLOWED, DEFAULT_FLOW_COUNT } from './constants';
import metricStore from '@globalStore/metricsStore';

export const ECO_WIDGET_TYPES = {
  networkResponse: 'network-response',
  networkDuration: 'network-duration',
  networkPercentile: 'network-percentile',
  autoRca: 'auto-rca',
  metricGroup: 'metric-group',
  flow: 'flow',
  qoe: 'qoe',
  improvementOpportunities: 'improvement-opportunities',
  impactedTime: 'impactedTime',
  eventMetricGroup: 'event-metric-group',
  anr: 'anr',
  crashes: 'crashes',
};

export const ECO_WIDGET_LIST = [
  {
    type: ECO_WIDGET_TYPES.qoe,
    meta: {
      title: 'QoE',
      limit: 1,
      context: ['any'],
      preview: PreviewQoE,
      availability: AVAILABILITY.BETA,
    },
    state: {},
  },
  {
    type: ECO_WIDGET_TYPES.improvementOpportunities,
    meta: {
      title: 'Improvement Opportunities',
      limit: 1,
      context: ['any'],
      preview: PreviewOpp,
      availability: AVAILABILITY.GA,
    },
    state: {},
  },
  {
    type: ECO_WIDGET_TYPES.impactedTime,
    meta: {
      title: 'User Time Insight',
      limit: 1,
      context: ['any'],
      preview: PreviewImpactedTime,
      availability: AVAILABILITY.GA,
    },
    state: {},
  },
  {
    type: ECO_WIDGET_TYPES.flow,
    meta: {
      title: 'Flow',
      limit: GLOBAL_MAX_WIDGETS_ALLOWED,
      context: ['any'], // list always
      preview: PreviewFlow,
      availability: AVAILABILITY.GA,
    },
    state: {
      flowId: null,
    },
  },
  {
    type: ECO_WIDGET_TYPES.metricGroup,
    meta: {
      title: 'Metric Group',
      limit: GLOBAL_MAX_WIDGETS_ALLOWED,
      context: ['any'], // list always
      preview: PreviewMetricGroup,
      availability: AVAILABILITY.GA,
    },
    state: {
      groupName: 'Metric Group',
      metrics: [],
    },
  },
  {
    type: ECO_WIDGET_TYPES.eventMetricGroup,
    meta: {
      title: 'Event',
      limit: GLOBAL_MAX_WIDGETS_ALLOWED,
      context: ['any'],
      preview: PreviewEvent,
      availability: AVAILABILITY.GA,
    },
    state: {
      groupName: 'Event ',
      metrics: [],
      eventName: null,
    },
  },
  {
    type: ECO_WIDGET_TYPES.networkResponse,
    meta: {
      title: 'Network Response',
      limit: 1,
      context: ['any', 'network'],
      preview: PreviewNetworkResponse,
      availability: AVAILABILITY.GA,
    },
    state: {},
  },
  {
    type: ECO_WIDGET_TYPES.networkDuration,
    meta: {
      title: 'Network Duration',
      limit: 1,
      context: ['any'],
      preview: PreviewNetworkDuration,
      availability: AVAILABILITY.GA,
    },
    state: {},
  },
  {
    type: ECO_WIDGET_TYPES.autoRca,
    meta: {
      title: 'Network Auto RCA',
      limit: 1,
      context: ['any'],
      preview: PreviewRCA,
      availability: AVAILABILITY.GA,
    },
    state: {
      lowerBound: 10,
    },
  },
  {
    type: ECO_WIDGET_TYPES.networkPercentile,
    meta: {
      title: 'Network Percentile',
      limit: 1,
      context: ['any'],
      preview: PreviewNetworkPercentile,
      availability: AVAILABILITY.GA,
    },
    state: {},
  },
  {
    type: ECO_WIDGET_TYPES.anr,
    meta: {
      title: 'ANR',
      limit: 1,
      context: ['any'],
      preview: PreviewANR,
      availability: AVAILABILITY.GA,
    },
    state: {
      groupName: 'ANR ',
      timeseriesMetrics: [metricKeys.anr_count, metricKeys.AppUniqueDevices],
      donutChartMetrics: [
        metricKeys.anr_to_session_end_success,
        metricKeys.anr_to_app_startup_success,
      ],
    },
  },
  {
    type: ECO_WIDGET_TYPES.crashes,
    meta: {
      title: 'Crashes',
      limit: 1,
      context: ['any'],
      preview: PreviewCrashes,
      availability: AVAILABILITY.GA,
    },
    state: {
      groupName: 'Crashes ',
      timeseriesMetrics: [metricKeys.AppCrashSessions, metricKeys.AppUniqueDevices],
      donutChartMetrics: [
        metricKeys.app_crash_to_session_end_success,
        metricKeys.crash_to_app_startup_success,
      ],
    },
  },
];

export const ECO_WIDGET_TYPE_MAP = {};
Object.values(ECO_WIDGET_LIST).forEach((w) => {
  ECO_WIDGET_TYPE_MAP[w.type] = w;
});

// configure default widgets, customize state if required
export const getEcoDefaultWidgets = () => {
  //Adding latest flow ids to default config as per DEFAULT_FLOW_COUNT constant
  const flowConfigMeta = metricStore.getState().flowConfigMeta;
  let flowIds: any = [];
  if (Object.keys(flowConfigMeta).length) {
    //get ids in descending order of creation time
    flowIds = Object.values(flowConfigMeta)
      .sort((a: any, b: any) => b.created_at - a.created_at)
      .slice(0, DEFAULT_FLOW_COUNT)
      .map((flow: any) => flow.id);
  }
  const ecoDefaultWidgets = {};
  const ecoDefaultWidgetsArray = [
    {
      ...ECO_WIDGET_TYPE_MAP[ECO_WIDGET_TYPES.metricGroup],
      state: {
        groupName: 'Audience',
        metrics: [
          metricKeys.AppSessions,
          metricKeys.UserActiveTime,
          metricKeys.AppSubscribers,
          metricKeys.AppUniqueDevices,
        ],
      },
    },
  ];
  if (flowIds.length) {
    for (const flowId of flowIds) {
      ecoDefaultWidgetsArray.push({
        ...ECO_WIDGET_TYPE_MAP[ECO_WIDGET_TYPES.flow],
        state: {
          flowId,
        },
      });
    }
  } else
    ecoDefaultWidgetsArray.push({
      ...ECO_WIDGET_TYPE_MAP[ECO_WIDGET_TYPES.flow],
    });

  ecoDefaultWidgetsArray.forEach((widget) => {
    const widgetConfig = buildWidgetConfig({ type: widget.type, state: widget.state });
    ecoDefaultWidgets[widgetConfig.wid] = widgetConfig;
  });

  return ecoDefaultWidgets;
};
